import React, { JSX, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import {
  PlusIcon,
  ArrowsRightLeftIcon,
  TrashIcon,
  PencilSquareIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { NumberInput } from 'components/NumberInput'
import { ColorInput, BackgroundInput } from 'components/ColorPickers'
import { ImageUpload } from 'components/ImageUpload'
import { ErrorInline } from 'components/ErrorInline'
import { SavedNote } from 'components/SavedNote'
import { ProviderBrandIcon, TWITCH_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { BackgroundImageInput, fitToObjectFit } from 'components/BackgroundImageInput'
import { ScaledStage, CenteredOnCanvas } from 'components/ScaledStage'
import { YouTubeDisclosureLine } from 'components/YouTubeDisclosureLine'
import { TWITCH_CHANNELS_OPTIONAL_HINT, eventHasYouTubeSource } from 'components/EventYouTubeSources'
import { TUG, elementFontStack } from 'lib/elementCanvas'
import {
  CHAT_FONT_OPTION_GROUPS,
  ChatFontKey,
  DEFAULT_CHAT_FONT_OPTION,
  normalizeChatFontKey
} from 'lib/chatFonts'
import { useFontGate } from 'lib/fontLoader'
import { elementDisplayName, sortByName } from 'lib/elementOrder'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { useEventDetail } from 'hooks/useEventDetail'
import { useYouTubeConnection } from 'hooks/useYouTubeConnection'
import { graphqlRequest } from 'lib/helpers'
import {
  VoteLaneMode,
  largestRemainderPercents,
  sumCounts,
  twitchChannelCountOf,
  voteLaneModeOf
} from 'lib/votePercent'
import {
  TugSummary,
  TugSide,
  TugSideInput,
  TugStatus,
  EventDetail,
  ChannelBinding,
  DeletionResult,
  ImageFit,
  VoteCountsByPlatform
} from 'types/api'
import createTugMutation from 'graphql/createTugOfWar.graphql'
import updateTugMutation from 'graphql/updateTugOfWar.graphql'
import deleteTugMutation from 'graphql/deleteTugOfWar.graphql'
import duplicateTugMutation from 'graphql/duplicateTugOfWar.graphql'

const DEFAULT_BG = 'transparent'
const DEFAULT_QUESTION_TEXT_COLOR = '#FFFFFF'
const DEFAULT_FINALIZED_TEXT = 'Results'
const DEFAULT_FINALIZED_TEXT_COLOR = '#FFFFFF'
const DEFAULT_SIDE_A_COLOR = '#7C3AED'
const DEFAULT_SIDE_B_COLOR = '#A78BFA'
const DEFAULT_SIDE_TEXT_COLOR = '#FFFFFF'
const DEFAULT_CHAT_COMMAND_BG = '#00000066'
const DEFAULT_BAR_HEIGHT = TUG.barHeight

const MAX_BAR_HEIGHT = 96

const DEFAULT_CORNER_RADIUS = 12
const MIN_CORNER_RADIUS = 0
const MAX_CORNER_RADIUS = 48
const DEFAULT_BORDER_WIDTH = 0
const DEFAULT_BORDER_COLOR = '#00000000'
const MIN_BORDER_WIDTH = 0
const MAX_BORDER_WIDTH = 24

const STATUS_PILL: Record<TugStatus, string> = {
  ACTIVE: 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/30',
  INACTIVE: 'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30'
}

const STATUS_LABEL: Record<TugStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive'
}

const SectionCard = ({
  title,
  children,
  action
}: { title: string, children: React.ReactNode, action?: React.ReactNode }): JSX.Element => (
  <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
    <div className='mb-4 flex items-center justify-between'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

const FieldLabel = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <span className='mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
    {children}
  </span>
)

interface TugDraftSide {
  displayText: string
  command: string
  color: string
  textColor: string
  imageUrl: string
}

export interface TugDraft {
  name: string
  question: string
  questionTextColor: string
  finalizedText: string
  finalizedTextColor: string
  backgroundColor: string
  chatCommandBackground: string
  barHeight: number
  optionA: TugDraftSide
  optionB: TugDraftSide
  backgroundImage: string
  backgroundImageFit: ImageFit
  cornerRadius: number
  borderWidth: number
  borderColor: string
  fontKey: ChatFontKey
}

export type TugRenderState = 'pre-active' | 'active' | 'finalized'

export interface TugLiveData {
  state: TugRenderState
  optionAVotes: number
  optionBVotes: number
  optionAVotesByPlatform?: VoteCountsByPlatform
  optionBVotesByPlatform?: VoteCountsByPlatform
  hasYouTubeVotes?: boolean
  laneMode?: VoteLaneMode
}

const tugLaneModeOf = (live: TugLiveData | undefined): VoteLaneMode => live == null
  ? 'TWITCH'
  : (live.laneMode ?? (live.hasYouTubeVotes === true ? 'SPLIT' : 'TWITCH'))

const tugPcts = (a: number, b: number): [number, number] => {
  if (!(a + b > 0)) return [50, 50]
  const [pa, pb] = largestRemainderPercents([a, b])
  return [pa, pb]
}

export const tugHasYouTubeVotes = (tug: TugSummary): boolean =>
  (tug.hasYouTubeVotes ?? false) ||
  (tug.optionA.voteCountByPlatform?.youtube ?? 0) > 0 ||
  (tug.optionB.voteCountByPlatform?.youtube ?? 0) > 0

export const tugRenderStateFromServer = (
  status: 'ACTIVE' | 'INACTIVE',
  stoppedAt: string | null
): TugRenderState => {
  if (status === 'ACTIVE') return 'active'
  if (stoppedAt != null) return 'finalized'
  return 'pre-active'
}

export const tugLiveFromSummary = (tug: TugSummary): TugLiveData => {
  const a = tug.optionA.voteCountByPlatform ?? { twitch: tug.optionA.voteCount, youtube: 0 }
  const b = tug.optionB.voteCountByPlatform ?? { twitch: tug.optionB.voteCount, youtube: 0 }
  const laneMode = voteLaneModeOf({
    twitchChannelCount: twitchChannelCountOf(tug.broadcasterUserIds),
    twitchTotal: sumCounts([a.twitch, b.twitch]),
    youtubeTotal: sumCounts([a.youtube, b.youtube])
  })
  const youtubeOnly = laneMode === 'YOUTUBE'
  return {
    state: tugRenderStateFromServer(tug.status, tug.stoppedAt),
    optionAVotes: youtubeOnly ? a.youtube : tug.optionA.voteCount,
    optionBVotes: youtubeOnly ? b.youtube : tug.optionB.voteCount,
    optionAVotesByPlatform: a,
    optionBVotesByPlatform: b,
    hasYouTubeVotes: tugHasYouTubeVotes(tug),
    laneMode
  }
}

const TugImageSlot = ({
  side,
  chatCommandBackground,
  position,
  pctText,
  frameRadius
}: {
  side: TugDraftSide
  chatCommandBackground: string
  position: 'left' | 'right'
  pctText: string | null
  frameRadius: number
}): JSX.Element => {
  const hasImage = side.imageUrl.length > 0
  const positionStyle: React.CSSProperties = position === 'left'
    ? { left: 0 }
    : { right: 0 }
  const imageRadius = position === 'left'
    ? `${frameRadius}px 0 0 ${frameRadius}px`
    : `0 ${frameRadius}px ${frameRadius}px 0`
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        width: `${TUG.imageSize}px`,
        height: `${TUG.imageSize}px`,
        ...positionStyle
      }}
    >
      {hasImage
        ? (
          <img
            src={side.imageUrl}
            alt=''
            aria-hidden='true'
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              borderRadius: imageRadius
            }}
            onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2' }}
          />
          )
        : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '8px',
              fontWeight: TUG.noImageText.fontWeight,
              fontSize: `${TUG.noImageText.fontSize}px`,
              color: side.textColor
            }}
          >
            {side.displayText.length > 0 ? side.displayText : '—'}
          </div>
          )}

      {pctText !== null && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: `${TUG.pill.width}px`,
            height: `${TUG.pill.height}px`,
            borderRadius: `${TUG.pill.height / 2}px`,
            background: chatCommandBackground,
            color: side.textColor,
            fontWeight: TUG.pill.fontWeight,
            fontSize: `${TUG.pill.fontSize}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {pctText}
        </div>
      )}
    </div>
  )
}

const TugResultsBar = ({
  draft,
  state,
  live,
  layoutWidth = TUG.width
}: {
  draft: TugDraft
  state: TugRenderState
  live?: TugLiveData
  layoutWidth?: number
}): JSX.Element => {
  const [pctA] = tugPcts(live?.optionAVotes ?? 0, live?.optionBVotes ?? 0)
  const barHeight = draft.barHeight ?? DEFAULT_BAR_HEIGHT
  const barLeft = TUG.imageSize + TUG.imageBarGap
  const barTop = (TUG.height - barHeight) / 2
  const barWidth = Math.max(200, layoutWidth - (TUG.width - TUG.barWidth))
  const fillALeft = (pctA / 100) * barWidth

  const barInset = 30

  const overlayText = state === 'pre-active'
    ? (draft.question.length > 0 ? draft.question : 'Your tug-of-war question')
    : state === 'finalized'
      ? (draft.finalizedText.length > 0 ? draft.finalizedText : DEFAULT_FINALIZED_TEXT)
      : null
  const overlayColor = state === 'finalized'
    ? draft.finalizedTextColor
    : draft.questionTextColor

  return (
    <div
      style={{
        position: 'absolute',
        left: `${barLeft}px`,
        top: `${barTop}px`,
        width: `${barWidth}px`,
        height: `${barHeight}px`,
        borderRadius: `${barHeight / 2}px`,
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: `${fillALeft}px`,
          height: '100%',
          background: draft.optionA.color,
          transition: 'width 180ms ease-out'
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: `${fillALeft}px`,
          top: 0,
          right: 0,
          height: '100%',
          background: draft.optionB.color,
          transition: 'left 180ms ease-out'
        }}
      />

      <div
        style={{
          position: 'absolute',
          left: `${barInset}px`,
          top: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          fontWeight: TUG.barText.fontWeight,
          fontSize: `${TUG.barText.fontSize}px`,
          lineHeight: `${TUG.barText.lineHeight}px`,
          color: draft.optionA.textColor
        }}
      >
        {draft.optionA.command !== '' ? draft.optionA.command : '#A'}
      </div>
      <div
        style={{
          position: 'absolute',
          right: `${barInset}px`,
          top: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          fontWeight: TUG.barText.fontWeight,
          fontSize: `${TUG.barText.fontSize}px`,
          lineHeight: `${TUG.barText.lineHeight}px`,
          color: draft.optionB.textColor
        }}
      >
        {draft.optionB.command !== '' ? draft.optionB.command : '#B'}
      </div>

      {overlayText !== null && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 16px',
            color: overlayColor,
            fontWeight: TUG.barText.fontWeight,
            fontSize: `${TUG.barText.fontSize}px`,
            lineHeight: `${TUG.barText.lineHeight}px`,
            textAlign: 'center',
            pointerEvents: 'none'
          }}
        >
          {overlayText}
        </div>
      )}
    </div>
  )
}

export const TugFrame = ({
  draft,
  label,
  state,
  live,
  layoutWidth = TUG.width
}: {
  draft: TugDraft
  label?: string
  state: TugRenderState
  live?: TugLiveData
  layoutWidth?: number
}): JSX.Element => {
  const hasBgImage = draft.backgroundImage.trim().length > 0
  const frameRadius = draft.cornerRadius
  const fontFamily = elementFontStack(draft.fontKey)
  return (
    <div>
      {label !== undefined && (
        <div className='mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
          {label}
        </div>
      )}
      <div
        style={{
          position: 'relative',
          width: `${layoutWidth}px`,
          height: `${TUG.height}px`,
          fontFamily
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: `${frameRadius}px`,
            overflow: 'hidden',
            background: draft.backgroundColor
          }}
        >
          {hasBgImage && (
            <img
              src={draft.backgroundImage}
              alt=''
              aria-hidden='true'
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: fitToObjectFit(draft.backgroundImageFit)
              }}
              onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2' }}
            />
          )}
        </div>

        {(() => {
          const a = live?.optionAVotes ?? 0
          const b = live?.optionBVotes ?? 0
          const perSource = tugLaneModeOf(live) === 'SPLIT'
          const [pctA, pctB] = tugPcts(a, b)
          return (
            <>
              <TugImageSlot
                side={draft.optionA}
                chatCommandBackground={draft.chatCommandBackground}
                position='left'
                pctText={perSource ? null : `${pctA}%`}
                frameRadius={frameRadius}
              />
              <TugResultsBar draft={draft} state={state} live={live} layoutWidth={layoutWidth} />
              <TugImageSlot
                side={draft.optionB}
                chatCommandBackground={draft.chatCommandBackground}
                position='right'
                pctText={perSource ? null : `${pctB}%`}
                frameRadius={frameRadius}
              />
            </>
          )
        })()}

        <div
          aria-hidden='true'
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: `${frameRadius}px`,
            border: `${draft.borderWidth ?? DEFAULT_BORDER_WIDTH}px solid ${draft.borderColor ?? DEFAULT_BORDER_COLOR}`,
            pointerEvents: 'none'
          }}
        />
      </div>
    </div>
  )
}

const TUG_TALLY_STYLE: React.CSSProperties = {
  marginTop: 8,
  display: 'flex',
  justifyContent: 'center',
  columnGap: 36,
  flexWrap: 'wrap',
  fontWeight: TUG.pill.fontWeight,
  fontSize: 18,
  lineHeight: 1.4,
  color: '#FFFFFF',
  textShadow: '0 1px 2px rgba(0,0,0,0.9)'
}

const sideTally = (side: TugDraftSide, fallback: string, counts?: VoteCountsByPlatform): string => {
  const label = side.displayText.length > 0 ? side.displayText : (side.command !== '' ? side.command : fallback)
  const twitch = counts?.twitch ?? 0
  const youtube = counts?.youtube ?? 0
  return `${label} — Twitch ${twitch} · YouTube ${youtube}`
}

const TugSourceTallies = ({ draft, live }: {
  draft: TugDraft
  live: TugLiveData
}): JSX.Element => (
  <div style={{ ...TUG_TALLY_STYLE, fontFamily: elementFontStack(draft.fontKey) }}>
    <span>{sideTally(draft.optionA, '#A', live.optionAVotesByPlatform)}</span>
    <span>{sideTally(draft.optionB, '#B', live.optionBVotesByPlatform)}</span>
  </div>
)

export const TugPreview = ({
  draft,
  live,
  layoutWidth = TUG.width,
  showYouTubeDisclosure = false
}: {
  draft: TugDraft
  live?: TugLiveData
  layoutWidth?: number
  showYouTubeDisclosure?: boolean
}): JSX.Element => {
  const fontReady = useFontGate(draft.fontKey)
  if (!fontReady) {
    return (
      <CenteredOnCanvas width={layoutWidth} height={live != null ? TUG.height : undefined}>
        {null}
      </CenteredOnCanvas>
    )
  }
  if (live != null) {
    const laneMode = tugLaneModeOf(live)
    const perSource = laneMode === 'SPLIT'
    const disclosed = live.hasYouTubeVotes === true || laneMode !== 'TWITCH'
    return (
      <CenteredOnCanvas width={layoutWidth} height={TUG.height}>
        <TugFrame draft={draft} state={live.state} live={live} layoutWidth={layoutWidth} />
        {perSource && <TugSourceTallies draft={draft} live={live} />}
        {(disclosed || showYouTubeDisclosure) && <YouTubeDisclosureLine />}
      </CenteredOnCanvas>
    )
  }

  return (
    <CenteredOnCanvas width={layoutWidth}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <TugFrame draft={draft} label='Pre-active (no votes yet)' state='pre-active' layoutWidth={layoutWidth} />
        <TugFrame draft={draft} label='Active (live voting)' state='active' layoutWidth={layoutWidth} />
        <TugFrame draft={draft} label='Finalized (votes counted)' state='finalized' layoutWidth={layoutWidth} />
      </div>
      {showYouTubeDisclosure && <YouTubeDisclosureLine />}
    </CenteredOnCanvas>
  )
}

const draftSideFromTug = (s: TugSide): TugDraftSide => ({
  displayText: s.displayText,
  command: s.command,
  color: s.color,
  textColor: s.textColor,
  imageUrl: s.imageUrl ?? ''
})

export const draftFromTug = (tug: TugSummary): TugDraft => ({
  name: tug.name,
  question: tug.question,
  questionTextColor: tug.questionTextColor,
  finalizedText: tug.finalizedText ?? '',
  finalizedTextColor: tug.finalizedTextColor,
  backgroundColor: tug.backgroundColor,
  chatCommandBackground: tug.chatCommandBackground,
  barHeight: tug.barHeight,
  optionA: draftSideFromTug(tug.optionA),
  optionB: draftSideFromTug(tug.optionB),
  backgroundImage: tug.backgroundImage ?? '',
  backgroundImageFit: tug.backgroundImageFit ?? 'COVER',
  cornerRadius: tug.cornerRadius,
  borderWidth: tug.borderWidth ?? DEFAULT_BORDER_WIDTH,
  borderColor: tug.borderColor ?? DEFAULT_BORDER_COLOR,
  fontKey: normalizeChatFontKey(tug.fontKey ?? 'DEFAULT')
})

const emptyDraft = (): TugDraft => ({
  name: '',
  question: '',
  questionTextColor: DEFAULT_QUESTION_TEXT_COLOR,
  finalizedText: DEFAULT_FINALIZED_TEXT,
  finalizedTextColor: DEFAULT_FINALIZED_TEXT_COLOR,
  backgroundColor: DEFAULT_BG,
  chatCommandBackground: DEFAULT_CHAT_COMMAND_BG,
  barHeight: DEFAULT_BAR_HEIGHT,
  optionA: {
    displayText: 'Team A',
    command: '#A',
    color: DEFAULT_SIDE_A_COLOR,
    textColor: DEFAULT_SIDE_TEXT_COLOR,
    imageUrl: ''
  },
  optionB: {
    displayText: 'Team B',
    command: '#B',
    color: DEFAULT_SIDE_B_COLOR,
    textColor: DEFAULT_SIDE_TEXT_COLOR,
    imageUrl: ''
  },
  backgroundImage: '',
  backgroundImageFit: 'COVER',
  cornerRadius: DEFAULT_CORNER_RADIUS,
  borderWidth: DEFAULT_BORDER_WIDTH,
  borderColor: DEFAULT_BORDER_COLOR,
  fontKey: 'DEFAULT'
})

const SideEditor = ({
  label,
  side,
  onChange
}: {
  label: string
  side: TugDraftSide
  onChange: (patch: Partial<TugDraftSide>) => void
}): JSX.Element => (
  <SectionCard title={label}>
    <div className='grid grid-cols-2 gap-3'>
      <label className='block'>
        <FieldLabel>Display text</FieldLabel>
        <input
          value={side.displayText}
          onChange={(e) => onChange({ displayText: e.target.value })}
          required
          className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
        />
      </label>
      <label className='block'>
        <FieldLabel>Chat command</FieldLabel>
        <input
          value={side.command}
          onChange={(e) => onChange({ command: e.target.value })}
          required
          className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 font-mono text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
        />
      </label>
      <ColorInput
        label='Side color'
        value={side.color}
        onChange={(v) => onChange({ color: v })}
        solidOnly
      />
      <ColorInput
        label='Side text color'
        value={side.textColor}
        onChange={(v) => onChange({ textColor: v })}
      />
    </div>
    <div className='mt-3'>
      <ImageUpload
        value={side.imageUrl}
        onChange={(v) => onChange({ imageUrl: v })}
        kind='TUG_SIDE'
        label='Image'
        helpText='Square images, ≤ 4 MB. PNG, JPEG, WebP, or GIF.'
      />
    </div>
  </SectionCard>
)

const TugPreviewBody = ({ draft, existing, readOnly }: {
  draft: TugDraft
  existing: TugSummary | null
  readOnly: boolean
}): JSX.Element => {
  const { youtubeConnected } = useYouTubeConnection()
  if (readOnly && existing !== null) {
    return (
      <>
        <ScaledStage
          mode='preview'
          showCheckerboard
          elementWidth={TUG.width}
          elementHeight={TUG.height + 24}
        >
          <TugPreview draft={draft} live={tugLiveFromSummary(existing)} />
        </ScaledStage>
        <p className='text-[11px] text-dm-text-accent'>
          Shows the tug-of-war&apos;s last recorded result.
        </p>
      </>
    )
  }
  return (
    <>
      <ScaledStage
        mode='preview'
        showCheckerboard
        elementWidth={TUG.width}
        elementHeight={3 * TUG.height + 2 * 16 + 3 * 20 + 24}
      >
        <TugPreview draft={draft} showYouTubeDisclosure={youtubeConnected} />
      </ScaledStage>
    </>
  )
}

const TugForm = ({
  event,
  existing,
  onSaved,
  onCancel
}: {
  event: EventDetail
  existing: TugSummary | null
  onSaved: (tug: TugSummary) => void
  onCancel: () => void
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const { account } = useEntitlements()
  const [draft, setDraft] = useState<TugDraft>(() =>
    existing !== null ? draftFromTug(existing) : emptyDraft()
  )
  const [broadcasterUserIds, setBroadcasterUserIds] = useState<string[]>(() =>
    existing !== null ? existing.broadcasterUserIds : event.channels.map((c) => c.broadcasterUserId)
  )
  const [dedupe, setDedupe] = useState<boolean>(() =>
    existing !== null ? existing.dedupe : true
  )
  const youtubeInvited = account?.youtubeChatInvited === true
  const showYouTubeOptIn = youtubeInvited || eventHasYouTubeSource(event)
  const [youtubeVotesEnabled, setYoutubeVotesEnabled] = useState<boolean>(() =>
    existing !== null ? existing.youtubeVotesEnabled === true : false
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState(false)
  useEffect(() => {
    setSavedNote(false)
  }, [draft, broadcasterUserIds, dedupe, youtubeVotesEnabled])

  const isEdit = existing !== null
  const readOnly = event.status === 'ENDED'

  const updateSide = (key: 'optionA' | 'optionB', patch: Partial<TugDraftSide>): void => {
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }))
  }

  const toggleChannel = (broadcasterUserId: string): void => {
    setBroadcasterUserIds((prev) => prev.includes(broadcasterUserId)
      ? prev.filter((id) => id !== broadcasterUserId)
      : [...prev, broadcasterUserId])
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (auth0Token === undefined || submitting) return
    if (draft.question.trim().length === 0) {
      setError('Question is required.')
      return
    }
    const cmdA = draft.optionA.command.trim().toLowerCase()
    const cmdB = draft.optionB.command.trim().toLowerCase()
    if (cmdA.length === 0 || cmdB.length === 0) {
      setError('Both sides need a chat command.')
      return
    }
    if (cmdA === cmdB) {
      setError('Side commands must be different.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const toSideInput = (s: TugDraftSide): TugSideInput => ({
        displayText: s.displayText,
        command: s.command.trim(),
        color: s.color,
        textColor: s.textColor,
        imageUrl: s.imageUrl.trim().length > 0 ? s.imageUrl.trim() : null
      })
      const sharedVars = {
        name: draft.name,
        broadcasterUserIds,
        question: draft.question,
        optionA: toSideInput(draft.optionA),
        optionB: toSideInput(draft.optionB),
        dedupe,
        youtubeVotesEnabled,
        questionTextColor: draft.questionTextColor,
        finalizedText: draft.finalizedText.trim().length > 0 ? draft.finalizedText : null,
        finalizedTextColor: draft.finalizedTextColor,
        backgroundColor: draft.backgroundColor,
        chatCommandBackground: draft.chatCommandBackground,
        barHeight: draft.barHeight,
        backgroundImage: draft.backgroundImage.trim().length > 0
          ? draft.backgroundImage
          : null,
        backgroundImageFit: draft.backgroundImage.trim().length > 0
          ? draft.backgroundImageFit
          : null,
        cornerRadius: draft.cornerRadius,
        borderWidth: draft.borderWidth,
        borderColor: draft.borderColor,
        fontKey: draft.fontKey === 'DEFAULT' ? null : draft.fontKey
      }
      if (isEdit) {
        const { tug } = await graphqlRequest<{ tug: TugSummary }>(
          updateTugMutation,
          { tugId: existing.tugId, ...sharedVars },
          auth0Token
        )
        setSavedNote(true)
        onSaved(tug)
      } else {
        const { tug } = await graphqlRequest<{ tug: TugSummary }>(
          createTugMutation,
          { eventId: event.eventId, ...sharedVars },
          auth0Token
        )
        setSavedNote(true)
        onSaved(tug)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save tug-of-war')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault()
        }
      }}
      className='mt-2 grid grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3'
    >
      <div className='space-y-1.5 lg:col-span-2'>
        <fieldset disabled={readOnly} className='contents space-y-1.5'>
          <SectionCard
            title={readOnly ? 'View tug-of-war' : isEdit ? 'Edit tug-of-war' : 'New tug-of-war'}
            action={
            readOnly
              ? (
                <span className='inline-flex items-center gap-1.5 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
                  <EyeIcon className='size-3' aria-hidden='true' />
                  Read-only
                </span>
                )
              : (
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                >
                  <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                  Back to list
                </button>
                )
          }
          >
            <label className='mb-4 block'>
              <FieldLabel>Name</FieldLabel>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder='Short label for this tug-of-war (shown in the list)'
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                Displayed in list. Defaults to the question text when empty.
              </p>
            </label>
            <label className='mb-4 block'>
              <FieldLabel>Pre-active question</FieldLabel>
              <input
                value={draft.question}
                onChange={(e) => setDraft((d) => ({ ...d, question: e.target.value }))}
                placeholder='Who won this round?'
                required
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                Shown on the overlay until you start the tug. Replaced by the live bar while
                active, and by the finalized text once votes are in.
              </p>
            </label>

            <label className='mb-4 block'>
              <FieldLabel>Finalized text</FieldLabel>
              <input
                value={draft.finalizedText}
                onChange={(e) => setDraft((d) => ({ ...d, finalizedText: e.target.value }))}
                placeholder={DEFAULT_FINALIZED_TEXT}
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
            </label>

            <div className='mb-4'>
              <FieldLabel>
                <span className='flex items-center gap-1.5'>
                  <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                  Twitch channels
                </span>
              </FieldLabel>
              {event.channels.length === 0
                ? (
                  <p className='rounded-md border border-dashed border-white/10 p-3 text-xs text-dm-text-secondary'>
                    No channels bound to this stream yet. Add one on the Integrations page.
                  </p>
                  )
                : (
                  <ul className='space-y-1.5'>
                    {event.channels.map((c: ChannelBinding) => (
                      <li key={c.broadcasterUserId}>
                        <label className='flex cursor-pointer items-center gap-2.5 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-sm hover:bg-dm-bg-accent/50'>
                          <input
                            type='checkbox'
                            checked={broadcasterUserIds.includes(c.broadcasterUserId)}
                            onChange={() => toggleChannel(c.broadcasterUserId)}
                            className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                          />
                          <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                          <span className='text-dm-text-primary'>{c.channelLogin}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  )}
              {showYouTubeOptIn && (
                <p className='mt-1.5 text-[11px] text-dm-text-accent' data-testid='twitch-channels-optional-hint'>
                  {TWITCH_CHANNELS_OPTIONAL_HINT}
                </p>
              )}
            </div>

            <label className='flex items-center gap-2 text-sm text-dm-text-primary'>
              <input
                type='checkbox'
                checked={dedupe}
                onChange={(e) => setDedupe(e.target.checked)}
                className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
              />
              One vote per user in chat (dedupe)
            </label>
            {showYouTubeOptIn && (
              <label className='mt-3 flex items-center gap-2 text-sm text-dm-text-primary'>
                <input
                  type='checkbox'
                  checked={youtubeVotesEnabled}
                  onChange={(e) => setYoutubeVotesEnabled(e.target.checked)}
                  className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                />
                Count YouTube votes (this stream&apos;s YouTube sources)
              </label>
            )}
          </SectionCard>

          <SideEditor
            label='Side A'
            side={draft.optionA}
            onChange={(patch) => updateSide('optionA', patch)}
          />
          <SideEditor
            label='Side B'
            side={draft.optionB}
            onChange={(patch) => updateSide('optionB', patch)}
          />

          <SectionCard title='Shared styling'>
            <label className='mb-4 block'>
              <FieldLabel>Font</FieldLabel>
              <select
                value={draft.fontKey}
                onChange={(e) => setDraft((d) => ({ ...d, fontKey: e.target.value as ChatFontKey }))}
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              >
                <option value={DEFAULT_CHAT_FONT_OPTION.key}>Default (standard element font)</option>
                {CHAT_FONT_OPTION_GROUPS.map((group) => (
                  <optgroup key={group.category} label={group.label}>
                    {group.options.map((o) => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <div>
                <BackgroundInput
                  label='Overlay background'
                  value={draft.backgroundColor}
                  onChange={(v) => setDraft((d) => ({ ...d, backgroundColor: v }))}
                />
              </div>
              <ColorInput
                label='Pre-active question color'
                value={draft.questionTextColor}
                onChange={(v) => setDraft((d) => ({ ...d, questionTextColor: v }))}
              />
              <ColorInput
                label='Finalized text color'
                value={draft.finalizedTextColor}
                onChange={(v) => setDraft((d) => ({ ...d, finalizedTextColor: v }))}
              />
              <ColorInput
                label='Chat command pill background'
                value={draft.chatCommandBackground}
                onChange={(v) => setDraft((d) => ({ ...d, chatCommandBackground: v }))}
              />
              <label className='block'>
                <FieldLabel>Bar height (px)</FieldLabel>
                <div className='flex items-center gap-2'>
                  <input
                    type='range'
                    min={0}
                    max={MAX_BAR_HEIGHT}
                    value={draft.barHeight}
                    onChange={(e) => setDraft((d) => ({ ...d, barHeight: Number.parseInt(e.target.value, 10) }))}
                    className='flex-1 accent-brand'
                  />
                  <NumberInput
                    value={draft.barHeight}
                    min={0}
                    max={MAX_BAR_HEIGHT}
                    onCommit={(n) => setDraft((d) => ({ ...d, barHeight: n }))}
                    className='w-20 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                  />
                </div>
              </label>
              <label className='block'>
                <FieldLabel>Container corner radius (px)</FieldLabel>
                <div className='flex items-center gap-2'>
                  <input
                    type='range'
                    min={MIN_CORNER_RADIUS}
                    max={MAX_CORNER_RADIUS}
                    value={draft.cornerRadius}
                    onChange={(e) => setDraft((d) => ({ ...d, cornerRadius: Number.parseInt(e.target.value, 10) }))}
                    className='flex-1 accent-brand'
                  />
                  <NumberInput
                    value={draft.cornerRadius}
                    min={MIN_CORNER_RADIUS}
                    max={MAX_CORNER_RADIUS}
                    onCommit={(n) => setDraft((d) => ({ ...d, cornerRadius: n }))}
                    className='w-20 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                  />
                </div>
              </label>
              <label className='block'>
                <FieldLabel>Border width (px)</FieldLabel>
                <NumberInput
                  value={draft.borderWidth}
                  min={MIN_BORDER_WIDTH}
                  max={MAX_BORDER_WIDTH}
                  onCommit={(n) => setDraft((d) => ({ ...d, borderWidth: n }))}
                  className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                />
              </label>
              <div>
                <ColorInput
                  label='Border color'
                  value={draft.borderColor}
                  onChange={(v) => setDraft((d) => ({ ...d, borderColor: v }))}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard title='Overlay background image'>
            <BackgroundImageInput
              url={draft.backgroundImage}
              fit={draft.backgroundImageFit}
              onChangeUrl={(v) => setDraft((d) => ({ ...d, backgroundImage: v }))}
              onChangeFit={(v) => setDraft((d) => ({ ...d, backgroundImageFit: v }))}
              kind='TUG_BACKGROUND'
              label='Background image (optional)'
            />
          </SectionCard>

        </fieldset>
      </div>
      <div className='lg:col-span-1'>
        <div className='sticky top-4 space-y-3'>
          {readOnly
            ? (
              <>
                <div className='rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary'>
                  This stream has ended, so its tug-of-wars are read-only. The
                  preview below shows the tug-of-war&apos;s last state.
                </div>
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                >
                  <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                  Back to list
                </button>
              </>
              )
            : (
              <div className='flex items-center gap-2'>
                <button
                  type='submit'
                  disabled={submitting}
                  className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {submitting && <LoadingSpinner />}
                  {isEdit ? 'Save changes' : 'Create tug-of-war'}
                </button>
                <button
                  type='button'
                  onClick={onCancel}
                  disabled={submitting}
                  className='inline-flex flex-1 items-center justify-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
                >
                  Cancel
                </button>
              </div>
              )}
          {error !== null && <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>}
          {savedNote && <SavedNote testId='tug-saved' />}
          <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            Preview
          </h4>
          <TugPreviewBody draft={draft} existing={existing} readOnly={readOnly} />
        </div>
      </div>
    </form>
  )
}

const tugPlatformTotals = (tug: TugSummary): { twitch: number, youtube: number } => ({
  twitch: sumCounts([
    tug.optionA.voteCountByPlatform?.twitch ?? tug.optionA.voteCount,
    tug.optionB.voteCountByPlatform?.twitch ?? tug.optionB.voteCount
  ]),
  youtube: sumCounts([
    tug.optionA.voteCountByPlatform?.youtube ?? 0,
    tug.optionB.voteCountByPlatform?.youtube ?? 0
  ])
})

const tugVoteFigure = (tug: TugSummary): string => {
  const { twitch, youtube } = tugPlatformTotals(tug)
  return youtube > 0 ? `Twitch ${twitch} · YouTube ${youtube}` : `${tug.totalVotes} votes`
}

const TugListRow = ({
  tug,
  onEdit,
  onDuplicate,
  onDelete,
  busyAction,
  readOnly
}: {
  tug: TugSummary
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
  busyAction: null | 'delete' | 'duplicate'
  readOnly: boolean
}): JSX.Element => {
  const isActive = tug.status === 'ACTIVE'
  return (
    <li
      data-testid={`tug-row-${tug.tugId}`}
      className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3'
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className={classNames(
            'shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset',
            STATUS_PILL[tug.status]
          )}
          >
            {STATUS_LABEL[tug.status]}
          </span>
          <div className='flex min-w-0 flex-col leading-tight'>
            <span className='truncate text-sm font-medium text-dm-text-primary' title={tug.question}>
              {elementDisplayName(tug.name, tug.question)}
            </span>
            <span className='truncate text-[11px] text-dm-text-accent'>
              {tug.optionA.command} vs {tug.optionB.command} · {tugVoteFigure(tug)}
              {tug.broadcasterUserIds.length > 1 ? ` · ${tug.broadcasterUserIds.length} channels` : ''}
            </span>
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          <button
            type='button'
            onClick={onEdit}
            disabled={busyAction !== null}
            title={readOnly ? 'View' : 'Edit'}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {readOnly
              ? <EyeIcon className='size-3.5' aria-hidden='true' />
              : <PencilSquareIcon className='size-3.5' aria-hidden='true' />}
            {readOnly ? 'View' : 'Edit'}
          </button>
          {!readOnly && (
            <button
              type='button'
              onClick={onDuplicate}
              title='Duplicate'
              disabled={busyAction !== null}
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {busyAction === 'duplicate' ? <LoadingSpinner /> : <DocumentDuplicateIcon className='size-3.5' aria-hidden='true' />}
              Duplicate
            </button>
          )}
          <button
            type='button'
            onClick={onDelete}
            disabled={busyAction !== null || isActive}
            title={isActive ? 'Stop this tug first, voting controls are in the overlay editor' : 'Delete'}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busyAction === 'delete' ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
            Delete
          </button>
        </div>
      </div>
    </li>
  )
}

type Mode =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'edit', tugId: string }

export const TugsPage = (): JSX.Element => {
  const { auth0Token } = useAuth()
  const { event, setEvent, refetchEvent } = useEventDetail()
  const isEnded = event.status === 'ENDED'
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [rowBusy, setRowBusy] = useState<{ tugId: string, action: 'delete' | 'duplicate' } | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const editing = useMemo<TugSummary | null>(() => {
    if (mode.kind !== 'edit') return null
    return event.tugs.find((t) => t.tugId === mode.tugId) ?? null
  }, [mode, event.tugs])

  const upsertTug = (tug: TugSummary): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({
          ...prev,
          tugs: prev.tugs.some((t) => t.tugId === tug.tugId)
            ? prev.tugs.map((t) => t.tugId === tug.tugId ? tug : t)
            : [...prev.tugs, tug]
        }))
  }

  const removeTug = (tugId: string): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({
          ...prev,
          tugs: prev.tugs.filter((t) => t.tugId !== tugId)
        }))
  }

  const handleDelete = async (tug: TugSummary): Promise<void> => {
    if (auth0Token === undefined || rowBusy !== null) return
    const totals = tugPlatformTotals(tug)
    const recorded = totals.youtube > 0
      ? `recorded votes (Twitch ${totals.twitch} · YouTube ${totals.youtube})`
      : `${tug.totalVotes} recorded vote${tug.totalVotes === 1 ? '' : 's'}`
    const ok = globalThis.confirm(
      tug.totalVotes > 0 || totals.twitch > 0 || totals.youtube > 0
        ? `Delete tug-of-war "${tug.question}"?\n\nWARNING: this tug-of-war has ${recorded}. Deleting it permanently destroys that vote data. It cannot be recovered. (Reset also destroys vote records; to keep results, create a new tug-of-war instead.)\n\nDelete and destroy the votes?`
        : `Delete tug-of-war "${tug.question}"? This cannot be undone.`
    )
    if (!ok) return
    setRowBusy({ tugId: tug.tugId, action: 'delete' })
    setRowError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deleteTugMutation, { tugId: tug.tugId }, auth0Token
      )
      if (result.deleted) removeTug(tug.tugId)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete tug-of-war')
    } finally {
      setRowBusy(null)
    }
  }

  const handleDuplicate = async (tug: TugSummary): Promise<void> => {
    if (auth0Token === undefined || rowBusy !== null) return
    setRowBusy({ tugId: tug.tugId, action: 'duplicate' })
    setRowError(null)
    try {
      const { tug: created } = await graphqlRequest<{ tug: TugSummary }>(
        duplicateTugMutation, { tugId: tug.tugId }, auth0Token
      )
      upsertTug(created)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to duplicate tug-of-war')
    } finally {
      setRowBusy(null)
    }
  }

  if (mode.kind === 'create') {
    return (
      <TugForm
        event={event}
        existing={null}
        onSaved={(tug) => { upsertTug(tug); setMode({ kind: 'edit', tugId: tug.tugId }) }}
        onCancel={() => setMode({ kind: 'list' })}
      />
    )
  }

  if (mode.kind === 'edit') {
    if (editing === null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>That tug-of-war no longer exists. Returning to the list…</ErrorInline>
          <button onClick={() => setMode({ kind: 'list' })} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            Back to list
          </button>
        </div>
      )
    }
    return (
      <TugForm
        event={event}
        existing={editing}
        onSaved={upsertTug}
        onCancel={() => setMode({ kind: 'list' })}
      />
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <SectionCard
        title='Tug of wars'
        action={
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => { void refetchEvent() }}
              title='Refresh'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
            >
              <ArrowPathIcon className='size-3.5' aria-hidden='true' />
              Refresh
            </button>
            {!isEnded && (
              <button
                type='button'
                data-testid='add-tug-button'
                onClick={() => setMode({ kind: 'create' })}
                className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
              >
                <PlusIcon className='size-3.5' aria-hidden='true' />
                Add new tug-of-war
              </button>
            )}
          </div>
        }
      >
        {rowError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}

        {event.tugs.length === 0
          ? (
            <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
              <ArrowsRightLeftIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
              <p>{isEnded ? 'This stream ended with no tug-of-wars.' : 'No tug-of-wars yet for this stream.'}</p>
              {!isEnded && (
                <button
                  type='button'
                  onClick={() => setMode({ kind: 'create' })}
                  className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
                >
                  <PlusIcon className='size-3.5' aria-hidden='true' />
                  Create the first one
                </button>
              )}
            </div>
            )
          : (
            <ul className='space-y-2' data-testid='tugs-list'>
              {sortByName(event.tugs, (t) => elementDisplayName(t.name, t.question), (t) => t.tugId).map((tug: TugSummary) => (
                <TugListRow
                  key={tug.tugId}
                  tug={tug}
                  onEdit={() => setMode({ kind: 'edit', tugId: tug.tugId })}
                  onDuplicate={() => { void handleDuplicate(tug) }}
                  onDelete={() => { void handleDelete(tug) }}
                  busyAction={rowBusy?.tugId === tug.tugId ? rowBusy.action : null}
                  readOnly={isEnded}
                />
              ))}
            </ul>
            )}
      </SectionCard>
    </div>
  )
}
